import { FC } from 'react'

interface PasswordStrengthMeterProps {
  /** Senha controlada (geralmente `formik.values.<campo>`). */
  password: string
  className?: string
}

/**
 * Medidor de força de senha acessível (WCAG 1.4.1 — não depende só de cor).
 *
 * Os critérios são os MESMOS do `senhaForteSchema` (comprimento >= 8, letra
 * minúscula, letra maiúscula, número e caractere especial `[@$!%*#?&]`), para que
 * "Forte" corresponda à política que de fato passa na validação.
 *
 * Acessibilidade: as barras coloridas são `aria-hidden` e o rótulo textual fica
 * num container `aria-live='polite'`. Como o texto do rótulo só muda quando o
 * NÍVEL muda (Fraca/Média/Forte), o leitor de tela não é anunciado a cada tecla
 * digitada — apenas na troca de categoria.
 */

const TOTAL_SEGMENTOS = 5

const calcularScore = (senha: string): number => {
  if (!senha) return 0
  let score = 0
  if (senha.length >= 8) score++
  if (/[a-z]/.test(senha)) score++
  if (/[A-Z]/.test(senha)) score++
  if (/\d/.test(senha)) score++
  if (/[@$!%*#?&]/.test(senha)) score++
  return score
}

const resolverNivel = (score: number): { label: string; cor: string } | null => {
  if (score === 0) return null
  if (score <= 2) return { label: 'Fraca', cor: 'bg-danger' }
  if (score <= 4) return { label: 'Média', cor: 'bg-warning' }
  return { label: 'Forte', cor: 'bg-success' }
}

export const PasswordStrengthMeter: FC<PasswordStrengthMeterProps> = ({
  password,
  className = '',
}) => {
  const score = calcularScore(password)
  const nivel = resolverNivel(score)

  return (
    <div className={className}>
      <div className='d-flex align-items-center mb-1' aria-hidden='true'>
        {Array.from({ length: TOTAL_SEGMENTOS }).map((_, index) => (
          <div
            key={index}
            className={`flex-grow-1 rounded h-5px ${index < TOTAL_SEGMENTOS - 1 ? 'me-2' : ''} ${
              nivel && index < score ? nivel.cor : 'bg-secondary'
            }`}
          />
        ))}
      </div>
      <div className='text-muted fs-8' role='status' aria-live='polite'>
        {nivel ? `Força da senha: ${nivel.label}` : ''}
      </div>
    </div>
  )
}
